import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import {
  Button,
  FormGroup,
  Input,
  Label,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { setUserSetting } from '../../actions/Auth';
import {
  clearFilesFiltersAndReload,
  clearFilesList, fetchFiles, setFilesFilters, setFilesParams,
} from '../../actions/Files';
import { refreshCollections, setCollectionFilters, setCollectionsParams } from '../../actions/Collections';

import {
  APIcreateUserSettingValue,
  APIremoveUserSettingValue,
  APIupdateUserSettingValue,
} from '../../api/User';

import { addFlashMessage } from '../../helpers/FlashMessages';
import {
  getMatchingSearches,
  getPinnedSearchSuggestedName,
  getTagTitle,
} from '../../helpers/Search';
import {
  filterParams, handleURLParams, handleURLParamsClear, prepareSearchFilters,
} from '../../helpers/URLParams';

import ErrorBoundary from '../ErrorBoundary';
import ProcessingSpinner from '../ProcessingSpinner';

const PinSearchModal = ({
  history,
  handleClose,
  pinnedSearch,
  mode,
  type,
  updateUserSetting,
  selectedFilters,
  updateFilesFilters,
  updateCollectionsFilters,
  resetFilesFilters,
  profile,
}) => {
  const [name, setName] = useState('');
  const [localSelectedFilters, setLocalSelectedFilters] = useState([]);
  const [nameError, setNameError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);

  const intl = useIntl();

  const otherMatchingSearches = getMatchingSearches(localSelectedFilters, type)
    .filter(s => s.uuid !== pinnedSearch.uuid);

  const handleChangeFilters = (filters) => {
    const newFilters = prepareSearchFilters(filters);
    handleURLParamsClear(filterParams, history);
    handleURLParams(newFilters, history);
    if (type === 'files') {
      resetFilesFilters();
      updateFilesFilters(newFilters);
    } else if (type === 'collections') {
      updateCollectionsFilters(newFilters);
    }
  };

  const handleAdd = async () => {
    setNameError(false);
    if (name.length === 0) {
      setNameError(true);
      return;
    }

    const uuid = uuidv4();

    setLoading(true);

    const settings = {
      name,
      uuid,
      type,
      filters: localSelectedFilters.map(sf => ({
        key: sf.key, type: sf.type, title: sf.title, label: sf.label,
      })),
    };

    try {
      const response = await APIcreateUserSettingValue(profile.uuid, 'pinned_searches', settings);
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'Created new pinned search' }),
      });
      updateUserSetting({ pinned_searches: response.data });
      handleClose();
    } catch {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    setNameError(false);
    if (name.length === 0) {
      setNameError(true);
      return;
    }

    setLoading(true);

    const search = otherMatchingSearches.length > 0 ? otherMatchingSearches[0] : pinnedSearch;

    const newFilters = localSelectedFilters;
    const setting = {
      ...search,
      filters: newFilters,
      name,
    };

    try {
      if (otherMatchingSearches.length > 0) {
        APIremoveUserSettingValue(profile.uuid, 'pinned_searches', pinnedSearch.uuid);
      }
      const response = await APIupdateUserSettingValue(profile.uuid, 'pinned_searches', setting);
      handleChangeFilters(newFilters);
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'Changed pinned search settings' }),
      });
      updateUserSetting({ pinned_searches: response.data });
      handleClose();
    } catch {
      setLoading(false);
    }
  };

  const removeFilter = (removeType, key) => {
    const newLocalSelectedFilters = localSelectedFilters.filter(sf => (
      !(sf.type === removeType && sf.key === key)
    ));
    setLocalSelectedFilters(newLocalSelectedFilters);
    const suggestedName = getPinnedSearchSuggestedName(newLocalSelectedFilters);
    if (mode === 'add' && !nameTouched) {
      setName(suggestedName);
    } else if (mode === 'manage') {
      const previousSuggestedName = getPinnedSearchSuggestedName(selectedFilters);
      if (previousSuggestedName === name) {
        setName(suggestedName);
      }
    }
  };

  const handleName = (value) => {
    setNameTouched(true);
    setName(value);
  };

  useEffect(() => {
    if (mode === 'manage') {
      setName(pinnedSearch.name);
      setLocalSelectedFilters(pinnedSearch.filters);
    }
    if (mode === 'add') {
      const suggestedName = getPinnedSearchSuggestedName(selectedFilters);
      setName(suggestedName);
      setLocalSelectedFilters(selectedFilters);
    }
  }, [mode, pinnedSearch.filters, pinnedSearch.name, selectedFilters]);

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {mode === 'add' && (intl.formatMessage({ defaultMessage: 'Save pinned search' }))}
        {mode === 'manage' && (intl.formatMessage({ defaultMessage: 'Edit pinned search' }))}
      </ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="searchName">
            {intl.formatMessage({ defaultMessage: 'Name' })}
          </Label>
          <Input
            type="text"
            name="name"
            id="searchName"
            placeholder={intl.formatMessage({ defaultMessage: 'Search name' })}
            value={name}
            onChange={e => handleName(e.target.value)}
            invalid={nameError}
          />
          {nameError && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {intl.formatMessage({ defaultMessage: 'Please provide a valid name' })}
            </div>
          )}
          {localSelectedFilters.length === 0 && (
            <div className="invalid-feedback" style={{ display: 'block' }}>
              {intl.formatMessage({ defaultMessage: 'No filters selected' })}
            </div>
          )}
        </FormGroup>
        <div className="active-filters tags-list">
          {localSelectedFilters.map((sf, idx) => (
            <div className="tag" key={`${sf.type}-${sf.key}`} title={getTagTitle(sf)}>
              <span className="tag-label">
                {sf.label}
              </span>
              {/* eslint-disable jsx-a11y/click-events-have-key-events */}
              <span
                className="tag-remove"
                title={intl.formatMessage({ defaultMessage: 'Remove' })}
                role="button"
                tabIndex={idx}
                onClick={() => { removeFilter(sf.type, sf.key); }}
              >
                <i className="far fa-times" />
              </span>
              {/* eslint-enable jsx-a11y/click-events-have-key-events */}
            </div>
          ))}
        </div>
        {(!loading && otherMatchingSearches.length > 0) && (
          <div className="invalid-feedback" style={{ display: 'block' }}>
            This search is already pinned as <strong>{otherMatchingSearches[0].name}</strong>.
            If you save the changes, you will overwrite the name.
          </div>
        )}
      </ModalBody>
      <ModalFooter className="justify-sb">
        <div className="buttons-group">
          {mode === 'manage' && (
          <Button
            color="secondary"
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          )}
        </div>
        <div className="buttons-group">
          {mode === 'manage' && (
            <Button
              color="primary"
              onClick={() => handleEdit()}
              disabled={loading || localSelectedFilters.length === 0}
            >
              <ProcessingSpinner show={loading} />
              {intl.formatMessage({ defaultMessage: 'Save changes' })}
            </Button>
          )}

          {mode === 'add' && (
            <>
              <Button
                color="secondary"
                onClick={handleClose}
              >
                {intl.formatMessage({ defaultMessage: 'Cancel' })}
              </Button>
              <Button
                color="primary"
                onClick={() => handleAdd()}
                disabled={loading || localSelectedFilters.length === 0}
              >
                <ProcessingSpinner show={loading} />
                {intl.formatMessage({ defaultMessage: 'Save' })}
              </Button>
            </>
          )}
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

const mapDispatchToProps = dispatch => ({
  updateUserSetting: (setting) => {
    dispatch(setUserSetting(setting));
  },
  updateFilesFilters: (updatedFilters) => {
    dispatch(setFilesFilters(updatedFilters));
    dispatch(clearFilesList());
    dispatch(setFilesParams({ page: 0 }));
    dispatch(fetchFiles());
  },
  updateCollectionsFilters: (updatedFilters) => {
    dispatch(setCollectionFilters(updatedFilters));
    dispatch(setCollectionsParams({ page: 0 }));
    dispatch(refreshCollections());
  },
  resetFilesFilters: () => {
    dispatch(clearFilesFiltersAndReload());
  },
});

PinSearchModal.propTypes = {
  history: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  updateUserSetting: PropTypes.func.isRequired,
  updateFilesFilters: PropTypes.func.isRequired,
  updateCollectionsFilters: PropTypes.func.isRequired,
  resetFilesFilters: PropTypes.func.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  profile: PropTypes.object.isRequired,
  pinnedSearch: PropTypes.object,
};

PinSearchModal.defaultProps = {
  pinnedSearch: {},
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PinSearchModal));
